<script setup lang="ts">
    import IconList from "@/components/calendars/overlayPanels/IconList/IconList.vue";
    import IconListItem from "@/components/calendars/overlayPanels/IconList/IconListItem.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import { formatProductTitle } from "@/tabs/Receptforskrivning/helpers";
    import moment from "moment";

    defineProps<{
        data: ReceptforskrivningDisplay;
    }>();

    const getDatetime = ({ tidpunkt }: { tidpunkt: string | null }) =>
        tidpunkt ? moment.parseZone(tidpunkt).format("yyyy-MM-DD HH:mm").toString() : null;
</script>

<template>
    <div class="recept-tab-uttag">
        <div v-if="data.antal_uttag > 0" class="info">
            <span>Uttag kvar: {{ `${data.antal_uttag_kvar} av ${data.antal_uttag}` }}</span>
            <span> Kvarvarande mängd: {{ data.mangd }} </span>
            <span>Antal förpackningar per uttag: {{ data.forpackningar_per_uttag }}</span>
        </div>
        <KSection
            v-for="uttag in data?.uttag"
            :key="uttag.id"
            :name="uttag.id"
            :expanded="false"
            :numbered="false"
            variant="boxed"
            chevronPlacement="left"
        >
            <template #heading>
                <div class="heading">
                    <template v-if="uttag.kreditering_expeditionsid">
                        <span>Kreditering</span>
                    </template>
                    <span>{{ getDatetime({ tidpunkt: uttag.expeditionstidpunkt }) }}</span>
                    <span v-if="!uttag.kreditering_expeditionsid">
                        {{ formatProductTitle({ artikelInfo: uttag.lakemedelInfo, isUttag: true }) }}
                    </span>
                </div>
            </template>
            <div class="section-content">
                <div class="left">
                    <IconList>
                        <IconListItem v-if="uttag.kreditering_expeditionsid" label="Krediteringstidpunkt">
                            {{
                                uttag.krediteringstidpunkt ? getDatetime({ tidpunkt: uttag.krediteringstidpunkt }) : "-"
                            }}
                        </IconListItem>
                        <IconListItem v-if="uttag.uttagna_forpackningar" label="Antal expedierade förpackningar">
                            {{ uttag.uttagna_forpackningar }}
                        </IconListItem>
                        <IconListItem v-if="uttag.uttagen_mangd" label="Expedierad mängd">
                            {{ uttag.uttagen_mangd }} {{ uttag.forpackningsenhet }}
                        </IconListItem>
                        <IconListItem v-if="uttag.uttagstyp" label="Uttagstyp">
                            {{ uttag.uttagstyp }}
                        </IconListItem>
                        <IconListItem v-if="uttag.substitution_utford" label="Generikautbyte utfört">
                            {{ "Ja" }}
                        </IconListItem>
                        <IconListItem v-if="uttag.uttagsnotering" label="Uttagsnotering">
                            {{ uttag.uttagsnotering }}
                        </IconListItem>
                    </IconList>
                </div>
                <div class="separator">
                    <div class="line" />
                </div>
                <div class="right">
                    <IconList>
                        <IconListItem v-if="uttag.formansval" label="Förmånsval">
                            {{ uttag.formansval ?? "" }}
                        </IconListItem>
                        <IconListItem label="Doseringsanvisning">
                            {{ uttag.doseringsanvisning ?? "" }}
                        </IconListItem>
                    </IconList>
                </div>
            </div>
        </KSection>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .recept-tab-uttag {
        display: flex;
        flex-direction: column;
        width: $content-max-width;
        max-width: 100%;

        .info {
            display: flex;
            gap: 2.5rem;
        }

        .heading {
            display: flex;
            gap: 1rem;
        }

        .section-content {
            display: flex;
            flex: 1;
            gap: 1.5rem;
            width: 100%;
            max-width: $content-max-width;

            .left,
            .right {
                display: grid;
                grid-auto-flow: row;
                grid-auto-rows: max-content;
                grid-template-columns: repeat(2, auto);
                grid-row-gap: 1.5rem;
                flex: 1;
                max-width: max-content;

                ::v-deep .icon-list {
                    display: grid;
                    grid-column: 1 / -1;
                    grid-template-columns: subgrid;
                }

                ::v-deep .icon-list-item {
                    grid-auto-flow: row;

                    .content {
                        width: 100%;
                    }
                }
            }

            .separator {
                .line {
                    border: 1px solid $divider-color;
                    height: 100%;
                }
            }
        }

        &.variant-simple {
            font-size: 0.9rem;
        }
    }
</style>
